import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Megaphone, Plus, Trash2, AlertTriangle, Info, Users, User as UserIcon, Bell } from 'lucide-react';
import {
  apiListAnnouncements, apiCreateAnnouncement, apiDeleteAnnouncement,
  apiMarkAnnouncementRead, apiListEmployees
} from '../api';
import { formatDateTime } from '../lib/formatters';
import { toast } from 'sonner';
import { logError } from '../lib/logger';

// ==================== HR VIEW ====================
export const AnnouncementManager = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const employeeOptions = useMemo(() => employees.filter(e => e.role === 'employee'), [employees]);

  const [form, setForm] = useState({ title: '', body: '', priority: 'normal', target: 'all', target_employee_id: '' });

  const reload = async () => {
    const [anns, emps] = await Promise.all([apiListAnnouncements(), apiListEmployees().catch(() => [])]);
    setList(anns || []);
    setEmployees(emps || []);
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => setForm({ title: '', body: '', priority: 'normal', target: 'all', target_employee_id: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) { toast.error(t('common.required')); return; }
    if (form.target === 'employee' && !form.target_employee_id) { toast.error(t('common.required')); return; }
    try {
      await apiCreateAnnouncement({
        title: form.title,
        body: form.body,
        priority: form.priority,
        target: form.target,
        target_employee_id: form.target === 'employee' ? form.target_employee_id : null
      });
      toast.success(t('hr.announcements.published'));
      setDialogOpen(false);
      resetForm();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.saveError'));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteAnnouncement(toDelete.id);
      toast.success(t('hr.announcements.deleted'));
      setToDelete(null);
      reload();
    } catch { toast.error(t('common.deleteError')); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">{t('hr.announcements.title')}</div>
          <div className="text-xs text-slate-500">{t('hr.announcements.priority')}</div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800 gap-2">
              <Plus className="w-4 h-4" /> {t('hr.announcements.newAnnouncement')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('hr.announcements.newAnnouncement')}</DialogTitle>
              <DialogDescription>
                {t('hr.announcements.priority')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="anntitle">{t('hr.announcements.titleLabel')} <span className="text-red-500">*</span></Label>
                <Input id="anntitle" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="annbody">{t('hr.announcements.body')} <span className="text-red-500">*</span></Label>
                <Textarea id="annbody" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required className="resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('hr.announcements.priority')}</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">{t('hr.announcements.priorityNormal')}</SelectItem>
                      <SelectItem value="urgent">{t('hr.announcements.urgent')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('common.select')}</Label>
                  <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      <SelectItem value="employee">{t('hr.dashboard.employees')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.target === 'employee' && (
                <div className="space-y-2">
                  <Label>{t('hr.dashboard.employees')} <span className="text-red-500">*</span></Label>
                  <Select value={form.target_employee_id} onValueChange={(v) => setForm({ ...form, target_employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder={t('common.select') + '...'} /></SelectTrigger>
                    <SelectContent>
                      {employeeOptions.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name} — {e.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button type="submit" className="bg-blue-700 hover:bg-blue-800">{t('common.save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">{t('hr.announcements.noAnnouncements')}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(a => {
            const isUrgent = a.priority === 'urgent';
            return (
              <div key={a.id} className={`p-4 rounded-xl border bg-white ${isUrgent ? 'border-red-200' : 'border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isUrgent ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {isUrgent ? <AlertTriangle className="w-5 h-5" /> : <Info className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-900">{a.title}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${isUrgent ? 'bg-red-50 text-red-800 border-red-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                        {isUrgent ? t('hr.announcements.urgent').toUpperCase() : t('hr.announcements.priorityNormal')}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                        {a.target === 'all' ? <><Users className="w-3 h-3" /> {t('common.all')}</> : <><UserIcon className="w-3 h-3" /> {a.target_employee_name}</>}
                      </span>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{a.body}</div>
                    <div className="text-[11px] text-slate-400 mt-2">{formatDateTime(a.created_at)} • {a.created_by}</div>
                  </div>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(a)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('hr.announcements.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.title}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== EMPLOYEE VIEW ====================
export const AnnouncementNotifications = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [urgentPopup, setUrgentPopup] = useState(null);

  const reload = async () => {
    try {
      const items = await apiListAnnouncements();
      setList(items || []);
      const firstUnreadUrgent = (items || []).find(a => a.priority === 'urgent' && !a.is_read);
      if (firstUnreadUrgent) setUrgentPopup(firstUnreadUrgent);
    } catch (e) {
      logError('Failed to load announcements:', e);
    }
  };

  useEffect(() => {
    reload();
    const timer = setInterval(reload, 60000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadCount = useMemo(() => list.filter(a => !a.is_read).length, [list]);
  const urgentCount = useMemo(() => list.filter(a => a.priority === 'urgent' && !a.is_read).length, [list]);

  const markRead = async (id) => {
    try {
      await apiMarkAnnouncementRead(id);
      setList(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
      if (urgentPopup?.id === id) setUrgentPopup(null);
    } catch (e) {
      logError('Failed to mark announcement as read:', e);
    }
  };

  const dismissPopup = async () => {
    if (urgentPopup) await markRead(urgentPopup.id);
    setUrgentPopup(null);
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(!open)}
          className={`relative ${urgentCount > 0 ? 'text-red-600 hover:text-red-700' : ''}`}
        >
          <Bell className={`w-5 h-5 ${urgentCount > 0 ? 'animate-pulse' : ''}`} />
          {unreadCount > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1 ${urgentCount > 0 ? 'bg-red-600' : 'bg-blue-600'}`}>
              {unreadCount}
            </span>
          )}
        </Button>

        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full mt-2 w-[380px] max-h-[500px] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl z-40">
              <div className="sticky top-0 bg-white px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="font-semibold text-sm text-slate-900">{t('hr.dashboard.announcements')}</div>
                {unreadCount > 0 && <div className="text-xs text-slate-500">{unreadCount}</div>}
              </div>
              {list.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">{t('hr.announcements.noAnnouncements')}</div>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {list.map(a => (
                    <div key={a.id} className={`p-3 ${!a.is_read ? 'bg-blue-50/40' : ''}`}>
                      <div className="flex items-start gap-2">
                        <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${a.priority === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                          {a.priority === 'urgent' ? <AlertTriangle className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="font-medium text-sm text-slate-900 truncate">{a.title}</span>
                            {!a.is_read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                          </div>
                          <div className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-3">{a.body}</div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-slate-400">{formatDateTime(a.created_at)}</span>
                            {!a.is_read && (
                              <button onClick={() => markRead(a.id)} className="text-[11px] text-blue-700 hover:underline">{t('common.ok')}</button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={!!urgentPopup} onOpenChange={(o) => { if (!o) dismissPopup(); }}>
        <DialogContent className="sm:max-w-md border-red-200">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-700 flex items-center justify-center flex-shrink-0 animate-pulse">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-block text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200 mb-1.5">
                {t('hr.announcements.urgent')}
              </div>
              <DialogTitle className="text-lg">{urgentPopup?.title}</DialogTitle>
              <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{urgentPopup?.body}</div>
              <div className="mt-3 text-[11px] text-slate-500">{urgentPopup && formatDateTime(urgentPopup.created_at)} • {urgentPopup?.created_by}</div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button onClick={dismissPopup} className="bg-red-600 hover:bg-red-700">{t('common.ok')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
